/**
 * Selects text at a touch point inside an epub.js iframe.
 * Strategy: walk up DOM for an element with an `id` (audio-sync sentence wrappers),
 * select its full text content. Falls back to the nearest block-level element,
 * then a single word at caret as a last resort.
 */

const BLOCK_TAGS = new Set([
  'P',
  'DIV',
  'LI',
  'TD',
  'TH',
  'BLOCKQUOTE',
  'H1',
  'H2',
  'H3',
  'H4',
  'H5',
  'H6',
  'SECTION',
  'ARTICLE',
]);

const textNodeRange = (iframeDoc: Document, el: Element): Range => {
  const range = iframeDoc.createRange();
  // Use text-node boundaries so Selection.toString() works correctly —
  // element-boundary ranges produce empty strings from Selection.toString() in Chrome.
  const walker = iframeDoc.createTreeWalker(el, NodeFilter.SHOW_TEXT);
  const firstText = walker.nextNode() as Text | null;
  let lastText = firstText;
  let node: Node | null;
  while ((node = walker.nextNode())) lastText = node as Text;
  if (firstText && lastText) {
    range.setStart(firstText, 0);
    range.setEnd(lastText, lastText.length);
  } else {
    range.selectNodeContents(el);
  }
  return range;
};

export const selectSentenceAtPoint = (iframeDoc: Document, x: number, y: number): Range | null => {
  const el = iframeDoc.elementFromPoint(x, y);
  if (!el) return null;

  // Walk up to find the nearest audio-sync sentence wrapper (has an id attribute)
  let cur: Element | null = el;
  for (let i = 0; i < 6; i++) {
    if (cur?.id) {
      return textNodeRange(iframeDoc, cur);
    }
    cur = cur?.parentElement ?? null;
  }

  // Fallback: walk up to the nearest block-level element and select its full text
  cur = el;
  for (let i = 0; i < 8; i++) {
    if (cur && BLOCK_TAGS.has(cur.tagName)) {
      return textNodeRange(iframeDoc, cur);
    }
    cur = cur?.parentElement ?? null;
  }

  // Last resort: single word at caret position
  const doc = iframeDoc as Document & {
    caretRangeFromPoint?: (x: number, y: number) => Range | null;
  };
  const caretRange = doc.caretRangeFromPoint?.(x, y) ?? null;
  if (!caretRange) {
    // Tap landed in whitespace — scan nearby block elements by vertical proximity
    return findNearestBlock(iframeDoc, x, y);
  }

  try {
    // @ts-ignore — expand() is not in lib.dom.d.ts but is widely supported
    caretRange.expand('word');
  } catch {
    // ignore if unsupported
  }

  return caretRange instanceof Range ? caretRange : null;
};

const findNearestBlock = (iframeDoc: Document, x: number, y: number): Range | null => {
  const candidates = Array.from(iframeDoc.querySelectorAll('p, li, blockquote, h1, h2, h3, h4, h5, h6'));
  let best: Element | null = null;
  let bestDist = Infinity;
  for (const el of candidates) {
    if (!el.textContent?.trim()) continue;
    const rect = el.getBoundingClientRect();
    const dy = Math.abs(rect.top + rect.height / 2 - y);
    const dx = Math.max(0, rect.left - x, x - rect.right);
    const dist = Math.hypot(dx, dy);
    if (dist < bestDist) {
      bestDist = dist;
      best = el;
    }
  }
  return best ? textNodeRange(iframeDoc, best) : null;
};
